import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './services/api';
import { getDesignerProfileForSession, designerProfileNoteLabel } from './services/landscapingQuestionsApi';
import InviteCollaboratorModal from './components/InviteCollaboratorModal';
import { X, Mail, Phone, MapPin, Lightbulb, Users, CheckCircle, Sparkles, Calendar, BookOpen, Star, Plus, Search, Trash2, Eye, EyeOff, Globe, Leaf, Image, ExternalLink, Loader, ClipboardList } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

function parseProjectDataJson(project) {
  if (!project?.project_data) return null;
  try {
    return typeof project.project_data === 'string' ? JSON.parse(project.project_data) : project.project_data;
  } catch {
    return null;
  }
}

const DesignerDashboard = ({ onLogout }) => {
  const [projects, setProjects] = useState([]);
  const [consultationRequests, setConsultationRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [error, setError] = useState('');
  const [showAddressBook, setShowAddressBook] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteMode, setInviteMode] = useState('manual'); // 'manual' or 'search'
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteType, setInviteType] = useState('trade');
  const [tradeCategory, setTradeCategory] = useState('contractor'); // Category for trades/suppliers
  const [selectedProjectId, setSelectedProjectId] = useState(''); // For Address Book invites
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [messageVisibility, setMessageVisibility] = useState('all');
  const [scheduleAccess, setScheduleAccess] = useState(true);
  const [designerLocation, setDesignerLocation] = useState('');
  const [selectedCollaboratorsForInvite, setSelectedCollaboratorsForInvite] = useState([]);
  const [showInviteToProjectsModal, setShowInviteToProjectsModal] = useState(false);
  const [invitationResults, setInvitationResults] = useState(null);
  const [showInvitationSummary, setShowInvitationSummary] = useState(false);
  const [imageModalPlant, setImageModalPlant] = useState(null);
  const [imageModalPhotos, setImageModalPhotos] = useState([]);
  const [imageModalLoading, setImageModalLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Community Q&A
  const [communityQA, setCommunityQA] = useState([]);
  const [loadingQA, setLoadingQA] = useState(false);
  const [expandedQA, setExpandedQA] = useState(null); // question id
  const [answerDraft, setAnswerDraft] = useState({}); // { [questionId]: { text, label, saving } }
  const [showQASection, setShowQASection] = useState(false);
  const [defaultDesignerNoteLabel, setDefaultDesignerNoteLabel] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    getDesignerProfileForSession()
      .then((p) => setDefaultDesignerNoteLabel(designerProfileNoteLabel(p)))
      .catch(() => setDefaultDesignerNoteLabel(''));
  }, []);

  const handleViewPlantImages = async (plant) => {
    setImageModalPlant(plant);
    setImageModalPhotos([]);
    setShowImageModal(true);
    setImageModalLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/plants/find-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: plant.plant_name,
          botanicalName: plant.botanical_name || null,
          commonName: plant.plant_name,
          verifiedOnly: false,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setImageModalPhotos(data.photos || []);
      }
    } catch (err) {
      console.error('Image search error:', err);
      setImageModalPhotos([]);
    } finally {
      setImageModalLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    loadConsultationRequests();
  }, []);

  const loadCommunityQA = async () => {
    setLoadingQA(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/designers/community-questions?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setCommunityQA(data.items || []);
    } catch (e) {
      console.error('community Q&A load error:', e);
    } finally {
      setLoadingQA(false);
    }
  };

  const handlePostAnswer = async (questionId) => {
    const draft = answerDraft[questionId] || {};
    const text = (draft.text || '').trim();
    if (!text) return;
    setAnswerDraft((d) => ({ ...d, [questionId]: { ...d[questionId], saving: true } }));
    try {
      const token = localStorage.getItem('authToken');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const label =
        (draft.label || '').trim() ||
        defaultDesignerNoteLabel ||
        userData.display_name ||
        userData.full_name ||
        'PlantADay Designer';
      const res = await fetch(`${API_BASE_URL}/designers/community-questions/${questionId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ advice_text: text, contributor_label: label }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to post answer');
      setAnswerDraft((d) => ({ ...d, [questionId]: { text: '', label: '', saving: false } }));
      await loadCommunityQA();
    } catch (e) {
      alert(e.message);
      setAnswerDraft((d) => ({ ...d, [questionId]: { ...d[questionId], saving: false } }));
    }
  };

  const loadCollaborators = async () => {
    try {
      setLoadingCollaborators(true);
      const response = await api.collaborationAPI.getAllCollaborators();
      setCollaborators(response.collaborators || []);
    } catch (err) {
      console.error('Error loading collaborators:', err);
      setCollaborators([]);
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const handleDeleteCollaborator = async (collab) => {
    if (!window.confirm(`Remove ${collab.recipient_name} from all projects? This will revoke their access.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to remove collaborators');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      // Delete all shares for this collaborator across all projects
      const shareIds = [];
      if (collab.projects) {
        collab.projects.forEach(project => {
          if (project.share_id) {
            shareIds.push(project.share_id);
          }
        });
      }

      // Revoke all shares
      const promises = shareIds.map(shareId =>
        fetch(`${API_BASE_URL}/collaboration/share/${shareId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch(err => {
          console.error(`Error deleting share ${shareId}:`, err);
          return { ok: false };
        })
      );

      const results = await Promise.allSettled(promises);
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;

      if (successful > 0) {
        alert(`Successfully removed ${collab.recipient_name} from ${successful} project${successful > 1 ? 's' : ''}`);
        loadCollaborators();
      } else {
        alert('Failed to remove collaborator. They may have already been removed.');
        loadCollaborators();
      }
    } catch (err) {
      console.error('Error removing collaborator:', err);
      alert('Failed to remove collaborator. Please try again.');
    }
  };

  const handleUpdateMessageVisibility = async (collab, visibility) => {
    // This would need share IDs - for now, show message
    alert('Message visibility updates need to be done per project. This feature will be enhanced soon.');
  };

  const handleUpdateScheduleAccess = async (collab, access) => {
    // This would need share IDs - for now, show message
    alert('Schedule access updates need to be done per project. This feature will be enhanced soon.');
  };

  const handleEditCollaborator = async (collab, updates) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to edit collaborators');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      // Update all shares for this collaborator
      const shareIds = [];
      if (collab.projects) {
        collab.projects.forEach(project => {
          if (project.share_id) {
            shareIds.push(project.share_id);
          }
        });
      }

      // Update all shares
      const sharePromises = shareIds.map(shareId =>
        fetch(`${API_BASE_URL}/collaboration/share/${shareId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            recipientName: updates.name,
            recipientEmail: updates.email,
            phoneNumber: updates.phone || null
          })
        }).catch(err => {
          console.error(`Error updating share ${shareId}:`, err);
          return { ok: false };
        })
      );

      // Also update project_trades if it's a trade
      const isTrade = collab.share_type === 'trade' || collab.trade_type;
      let tradePromises = [];
      if (isTrade && collab.projects) {
        tradePromises = collab.projects.map(project =>
          fetch(`${API_BASE_URL}/collaboration/update-trade`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              tradeEmail: collab.recipient_email,
              newEmail: updates.email,
              newName: updates.name,
              projectId: project.project_id
            })
          }).catch(err => {
            console.error(`Error updating trade for project ${project.project_id}:`, err);
            return { ok: false };
          })
        );
      }

      const results = await Promise.allSettled([...sharePromises, ...tradePromises]);
      const successful = results.filter(r => r.status === 'fulfilled' && r.value?.ok).length;

      if (successful > 0) {
        alert(`Successfully updated ${collab.recipient_name}`);
        loadCollaborators();
      } else {
        alert('Failed to update collaborator. Please try again.');
      }
    } catch (err) {
      console.error('Error editing collaborator:', err);
      alert('Failed to edit collaborator. Please try again.');
    }
  };

  const handleInviteToProjects = async (selectedProjectIds, inviteType, tradeCategory, messageVisibility, scheduleAccess) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to invite collaborators');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      // Invite each selected collaborator to each selected project
      const promises = [];
      for (const collab of selectedCollaboratorsForInvite) {
        for (const projectId of selectedProjectIds) {
          // Check if already invited to this project
          const alreadyInvited = collab.projects?.some(p => p.project_id === projectId);
          if (alreadyInvited) continue;

          const isTrade = collab.share_type === 'trade' || collab.trade_type;
          const inviteData = {
            projectId,
            recipientEmail: collab.recipient_email,
            recipientName: collab.recipient_name,
            accessLevel: 'comment',
            shareType: isTrade ? 'trade' : 'client',
            ...(isTrade && { tradeType: collab.trade_type || tradeCategory }),
            messageVisibility: messageVisibility || 'all',
            scheduleAccess: scheduleAccess !== undefined ? scheduleAccess : true
          };

          promises.push(
            fetch(`${API_BASE_URL}/collaboration/share`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(inviteData)
            }).then(async (response) => {
              if (response.ok) {
                const data = await response.json();
                return { ok: true, data, collaborator: collab, projectId };
              }
              return { ok: false, data: null, collaborator: collab, projectId };
            }).catch(err => {
              console.error('Error inviting:', err);
              return { ok: false, error: err, collaborator: collab, projectId };
            })
          );
        }
      }

      const results = await Promise.all(promises);
      
      // Collect successful invitations with details
      const successfulInvitations = [];
      const failedInvitations = [];
      
      for (const result of results) {
        const project = projects.find(p => p.id === result.projectId);
        
        if (result.ok && result.data) {
          successfulInvitations.push({
            collaborator: result.collaborator,
            project: project,
            shareUrl: result.data.share?.shareUrl || `${window.location.origin}/shared/${result.data.share?.shareToken}/communication`,
            shareToken: result.data.share?.shareToken
          });
        } else {
          failedInvitations.push({
            collaborator: result.collaborator,
            project: project
          });
        }
      }

      if (successfulInvitations.length > 0) {
        // Show summary modal with invitation details
        setInvitationResults({
          successful: successfulInvitations,
          failed: failedInvitations
        });
        setShowInvitationSummary(true);
        loadCollaborators();
      } else {
        alert('Failed to invite collaborators. They may already be invited to those projects.');
      }

      setShowInviteToProjectsModal(false);
      setSelectedCollaboratorsForInvite([]);
    } catch (error) {
      console.error('Error inviting to projects:', error);
      alert(`Failed to invite collaborators: ${error.message}`);
    }
  };

  // Google Business search
  const handleSearchBusinesses = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      alert('Please enter a search term');
      return;
    }

    setSearching(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      
      const params = new URLSearchParams({
        query: searchQuery,
        type: searchType
      });
      
      if (searchLocation) {
        params.append('location', searchLocation);
      }

      const response = await fetch(`${API_BASE_URL}/google-business/search?${params.toString()}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // If Google Places not configured, offer AI fallback
        if (errorData.error && errorData.error.includes('Google Places API key not configured')) {
          const useAI = window.confirm(
            'Google Places API is not set up.\n\n' +
            'Would you like to use AI to suggest businesses instead?\n\n' +
            'Note: AI suggestions won\'t include contact information - you\'ll need to find that manually.'
          );
          
          if (useAI) {
            params.append('useAI', 'true');
            const aiResponse = await fetch(`${API_BASE_URL}/google-business/search?${params.toString()}`, {
              headers: {
                ...(token && { 'Authorization': `Bearer ${token}` })
              }
            });
            
            if (aiResponse.ok) {
              const aiData = await aiResponse.json();
              setSearchResults(aiData.results || []);
              if (aiData.note) {
                alert(aiData.note);
              }
              return;
            }
          }
        }
        
        throw new Error(errorData.error || 'Search failed');
      }

      const data = await response.json();
      setSearchResults(data.results || data.businesses || []);
    } catch (error) {
      console.error('Error searching businesses:', error);
      alert(`Search failed: ${error.message}`);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleInviteFromSearch = async (business) => {
    // Use actual email if available, otherwise try to construct from website or use placeholder
    let email = business.email || '';
    
    if (!email && business.website) {
      // Try to construct email from website domain
      const domain = business.website.replace(/^https?:\/\//, '').split('/')[0];
      email = `contact@${domain}`;
    }
    
    setInviteName(business.name);
    setInviteEmail(email);
    setInviteType('trade');
    
    // Set category based on business type from search, or default to contractor
    if (business.business_type || business.type) {
      setTradeCategory(business.business_type || business.type || 'contractor');
    } else if (searchType && searchType !== 'all') {
      setTradeCategory(searchType);
    } else {
      setTradeCategory('contractor');
    }
    
    setInviteMode('manual'); // Switch to manual tab
    
    // If email is missing, show a helpful message
    if (!email && !business.email) {
      setTimeout(() => {
        alert(`Business: ${business.name}\n\nEmail not found. Please enter the email address manually, or use the "Open Email" button to compose an email.`);
      }, 100);
    }
  };

  const handleAddBusiness = async (businessData) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/google-business/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(businessData)
      });

      if (!response.ok) {
        throw new Error('Failed to add business');
      }

      alert('Business added! It will now appear in search results.');
      
      // Refresh search if there was a query
      if (searchQuery.trim()) {
        setSearching(true);
        try {
          const params = new URLSearchParams({
            query: searchQuery,
            type: searchType
          });
          
          if (searchLocation) {
            params.append('location', searchLocation);
          }

          const refreshResponse = await fetch(`${API_BASE_URL}/google-business/search?${params.toString()}`, {
            headers: {
              ...(token && { 'Authorization': `Bearer ${token}` })
            }
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setSearchResults(refreshData.results || []);
          }
        } catch (err) {
          console.error('Error refreshing search:', err);
        } finally {
          setSearching(false);
        }
      }
    } catch (error) {
      alert('Failed to add business. Please try again.');
      console.error('Error adding business:', error);
      throw error;
    }
  };

  // Load designer location for default search area
  const loadDesignerLocation = async () => {
    try {
      const userData = localStorage.getItem('userData') || localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        try {
          const API_BASE_URL = process.env.REACT_APP_API_URL || 
            (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
          const response = await fetch(`${API_BASE_URL}/designers/profile`, {
            headers: {
              ...(localStorage.getItem('authToken') && { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` })
            }
          });
          if (response.ok) {
            const profile = await response.json();
            const location = [];
            if (profile.city) location.push(profile.city);
            if (profile.provinceState) location.push(profile.provinceState);
            if (profile.country) location.push(profile.country);
            if (location.length > 0) {
              setDesignerLocation(location.join(', '));
              setSearchLocation(location.join(', '));
            }
          }
        } catch (err) {
          if (user.city) {
            setDesignerLocation(user.city);
            setSearchLocation(user.city);
          }
        }
      }
    } catch (error) {
      console.error('Error loading designer location:', error);
    }
  };

  const handleInvite = async (projectId) => {
    if (!inviteName.trim() || !inviteEmail.trim()) {
      alert('Please enter both name and email');
      return;
    }

    if (!projectId) {
      alert('Please select a project to invite this collaborator to');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to invite collaborators');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      const url = inviteType === 'trade'
        ? `${API_BASE_URL}/collaboration/invite-trade`
        : `${API_BASE_URL}/collaboration/share`;

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const body = inviteType === 'trade'
        ? {
            projectId: projectId,
            tradeEmail: inviteEmail.trim().toLowerCase(),
            tradeName: inviteName.trim(),
            tradeType: tradeCategory || 'contractor',
            accessLevel: 'comment',
            message: 'You have been invited to collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: scheduleAccess
          }
        : {
            projectId: projectId,
            recipientEmail: inviteEmail.trim().toLowerCase(),
            recipientName: inviteName.trim(),
            accessLevel: 'comment',
            message: 'You have been invited to view and collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: true // Clients always have schedule access
          };

      console.log('Inviting collaborator with body:', body);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to invite collaborator';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          
          // Handle specific error cases
          if (errorData.existingShare) {
            errorMessage = 'This collaborator is already invited to this project.';
          } else if (response.status === 400) {
            errorMessage = errorData.error || 'Invalid request. Please check all fields are filled correctly.';
          } else if (response.status === 403) {
            errorMessage = 'You do not have permission to invite to this project.';
          } else if (response.status === 404) {
            errorMessage = 'Project not found.';
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      const shareToken = data.share?.shareToken || data.trade?.share_token || data.share_token;
      const shareUrl = `${window.location.origin}/shared/${shareToken}/communication`;
      
      // Reset form
      setInviteEmail('');
      setInviteName('');
      setInviteType('client');
      setTradeCategory('contractor');
      setMessageVisibility('all');
      setScheduleAccess(true);
      setShowInviteModal(false);
      setSelectedProjectId('');
      
      // Show success modal with link and email option
      const emailBody = `Hi ${inviteName},\n\nYou have been invited to collaborate on a project.\n\nPlease click the link below to access:\n\n${shareUrl}\n\nThank you!`;
      const mailtoLink = `mailto:${inviteEmail}?subject=Project Collaboration Invitation&body=${encodeURIComponent(emailBody)}`;
      
      const copyLink = () => {
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      };
      
      const openEmail = () => {
        window.location.href = mailtoLink;
      };
      
      const userChoice = window.confirm(
        `Invitation created!\n\n` +
        `Link: ${shareUrl}\n\n` +
        `Click OK to open your email client with the invitation, or Cancel to just copy the link.`
      );
      
      if (userChoice) {
        openEmail();
      } else {
        copyLink();
      }
      
      // Refresh the collaborators list
      loadCollaborators();
    } catch (error) {
      console.error('Error inviting collaborator:', error);
      alert('Failed to invite collaborator. Please try again.');
    }
  };

  const loadConsultationRequests = async () => {
    try {
      setLoadingRequests(true);
      const userDataStr = localStorage.getItem('userData') || localStorage.getItem('user');
      if (!userDataStr) return;
      
      const user = JSON.parse(userDataStr);
      const userId = user.id || user.user_id || user.designer_id;
      if (!userId) return;

      const response = await api.yardAnalysisAPI.getRequests(userId);
      setConsultationRequests(response.requests || []);
    } catch (err) {
      console.error('Error loading consultation requests:', err);
      // Don't show error - this is optional
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleRequestClick = async (request) => {
    setSelectedRequest(request);
    // Mark as viewed if still pending
    if (request.status === 'pending') {
      try {
        await api.yardAnalysisAPI.updateStatus(request.id, 'viewed');
        setConsultationRequests(prev => 
          prev.map(r => r.id === request.id ? { ...r, status: 'viewed', viewed_at: new Date().toISOString() } : r)
        );
      } catch (err) {
        console.error('Error updating request status:', err);
      }
    }
  };

  const handleMarkResponded = async (requestId) => {
    try {
      await api.yardAnalysisAPI.updateStatus(requestId, 'responded');
      setConsultationRequests(prev => 
        prev.map(r => r.id === requestId ? { ...r, status: 'responded', responded_at: new Date().toISOString() } : r)
      );
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: 'responded', responded_at: new Date().toISOString() });
      }
    } catch (err) {
      console.error('Error updating request status:', err);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Check both 'user' and 'userData' for compatibility
      const userDataStr = localStorage.getItem('userData') || localStorage.getItem('user');
      if (!userDataStr) {
        setError('Please log in to view your projects');
        setLoading(false);
        navigate('/');
        return;
      }
      
      const user = JSON.parse(userDataStr);
      const userId = user.id || user.user_id || user.designer_id;
      
      if (!userId) {
        setError('User ID not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await api.getDesignerProjects(userId);
      setProjects(response.projects || []);
    } catch (err) {
      console.error('Error loading projects:', err);
      if (err.message && err.message.includes('Failed to fetch')) {
        setError('Unable to connect to server. Please make sure the backend server is running.');
      } else if (err.message && (err.message.includes('Invalid token') || err.message.includes('Authentication required'))) {
        setError('Your session has expired. Please log out and log back in.');
        // Auto-redirect will happen via api.js, but show message first
      } else {
        setError('Failed to load projects: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    navigate('/upload-plan');
  };

  const handleProjectClick = (projectId) => {
    navigate(`/plan/${projectId}`);
  };

  const pendingHomeownerBriefs = projects.filter((p) => {
    const pd = parseProjectDataJson(p);
    return pd?.homeowner_brief?.source === 'public_site' && pd?.homeowner_brief?.unopened === true;
  });

  const mainGridProjects = projects.filter((p) => {
    const pd = parseProjectDataJson(p);
    return !(pd?.homeowner_brief?.source === 'public_site' && pd?.homeowner_brief?.unopened === true);
  });

  const handleOpenHomeownerBrief = async (projectId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Please log in again.');
        return;
      }
      const res = await fetch(`${API_BASE_URL}/projects/${projectId}/homeowner-brief/open`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not open brief');
      }
      await loadProjects();
      navigate(`/plan/${projectId}`);
    } catch (e) {
      alert(e.message || 'Something went wrong');
    }
  };

  const handleDeleteProject = async (projectId, projectName, e) => {
    e.stopPropagation(); // Prevent triggering the card click
    if (
      !window.confirm(
        `Delete project "${projectName}"?\n\n` +
          'This cannot be undone. You will permanently lose:\n' +
          '• Project Plant List (master_plant_list) including AI care you filled\n' +
          '• Plant placements on plans\n' +
          '• Drawings/plan zones and notes tied to this project\n' +
          '• Project files\n' +
          '• Garden handoffs staged for this project\n\n' +
          'Your personal Designer Master Plant List and global plant library entries are NOT deleted.\n\n' +
          'Click OK only if you have exported or backed up anything you still need.'
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to delete projects');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete project');
      }

      // Reload projects
      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert(`Failed to delete project: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'draft': 'bg-gray-200 text-gray-700',
      'in_progress': 'bg-blue-200 text-blue-700',
      'completed': 'bg-green-200 text-green-700'
    };
    return statusColors[status] || statusColors['draft'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Projects</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage your landscape design projects
              </p>
              <p className="mt-2 text-xs text-gray-500 max-w-2xl leading-relaxed">
                <strong>Master Plant List</strong> (toolbar) is your catalog from plants across all projects.
                Each project has its own <strong>Project Plant List</strong>.
                A future <strong>Super Master Plant List</strong> (all designers, all projects) will appear in setup when it is ready.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/plants')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                PlantaDay
              </button>
              <button
                onClick={() => navigate('/master-plant-list')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                title="Your Master Plant List: plants gathered from all your projects, plus imports"
              >
                <Leaf className="mr-2 -ml-1 h-5 w-5" />
                Master Plant List
              </button>
              <button
                onClick={() => {
                  setShowAddressBook(true);
                  loadCollaborators();
                  loadDesignerLocation();
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <BookOpen className="mr-2 -ml-1 h-5 w-5" />
                Address Book
              </button>
              <button
                onClick={() => navigate('/suppliers')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Users className="mr-2 -ml-1 h-5 w-5" />
                Suppliers
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                My Profile
              </button>
              <button
                onClick={handleUploadClick}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New Project
              </button>
              {onLogout && (
                <button
                  onClick={() => {
                    onLogout();
                    // Use window.location to force full page reload and clear state
                    window.location.href = '/';
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Consultation Requests Section */}
        {consultationRequests.length > 0 && (
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Lightbulb className="w-6 h-6 text-green-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Yard Consultation Requests
                  </h2>
                  <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                    {consultationRequests.filter(r => r.status === 'pending').length} New
                  </span>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                Homeowners have requested consultations after getting AI yard analysis. Review their yard photos and AI insights.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {consultationRequests.map((request) => (
                  <div
                    key={request.id}
                    onClick={() => handleRequestClick(request)}
                    className={`border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow ${
                      request.status === 'pending' 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {request.homeowner_name || 'Homeowner'}
                        </h3>
                        <p className="text-sm text-gray-600">{request.homeowner_email}</p>
                      </div>
                      {request.status === 'pending' && (
                        <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                    {request.user_location && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {request.user_location.city || ''} {request.user_location.state || ''}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      {formatDate(request.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Community Q&A Section */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <button
              type="button"
              onClick={() => {
                setShowQASection((v) => !v);
                if (!showQASection && communityQA.length === 0) loadCommunityQA();
              }}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-green-600" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Community Q&amp;A</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Homeowner questions with AI answers — add your expert note</p>
                </div>
              </div>
              <span className="text-gray-400 text-lg">{showQASection ? '▲' : '▼'}</span>
            </button>

            {showQASection && (
              <div className="mt-6">
                {loadingQA ? (
                  <div className="flex items-center gap-2 text-gray-500 py-4">
                    <Loader className="w-5 h-5 animate-spin" /> Loading questions…
                  </div>
                ) : communityQA.length === 0 ? (
                  <p className="text-gray-500 text-sm">No questions yet. Check back soon.</p>
                ) : (
                  <div className="space-y-3">
                    {communityQA.map((q) => {
                      const isOpen = expandedQA === q.id;
                      const draft = answerDraft[q.id] || {};
                      const location = q.location_json ? (() => { try { return JSON.parse(q.location_json); } catch { return null; } })() : null;
                      return (
                        <div key={q.id} className={`border rounded-xl overflow-hidden transition-all ${isOpen ? 'border-green-300' : 'border-gray-200'}`}>
                          {/* Question header */}
                          <button
                            type="button"
                            onClick={() => {
                              const nextOpen = isOpen ? null : q.id;
                              setExpandedQA(nextOpen);
                              if (!isOpen && defaultDesignerNoteLabel) {
                                setAnswerDraft((d) => {
                                  const cur = d[q.id] || {};
                                  if ((cur.label || '').trim()) return d;
                                  return { ...d, [q.id]: { ...cur, label: defaultDesignerNoteLabel } };
                                });
                              }
                            }}
                            className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-gray-50"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 font-medium line-clamp-2">{q.question_text}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                {q.has_photo && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
                                    <Image className="w-3 h-3" /> Photo included
                                  </span>
                                )}
                                {location && (
                                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                    <MapPin className="w-3 h-3" />{[location.city, location.state].filter(Boolean).join(', ')}
                                  </span>
                                )}
                                <span className="text-xs text-gray-400">
                                  {Number(q.total_notes) > 0 ? `${q.published_notes} designer note${Number(q.published_notes) !== 1 ? 's' : ''}` : 'No designer notes yet'}
                                </span>
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                              <span className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white font-semibold">
                                Answer
                              </span>
                              <span className="text-gray-400">{isOpen ? '▲' : '▼'}</span>
                            </div>
                          </button>

                          {/* Expanded content */}
                          {isOpen && (
                            <div className="border-t border-gray-100 px-5 pb-5 space-y-4 bg-gray-50/40">

                              {/* Photo (if any) */}
                              {q.has_photo && q.photo_data && (
                                <div className="pt-4">
                                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Homeowner photo</p>
                                  <img
                                    src={q.photo_data}
                                    alt="Homeowner's yard"
                                    className="max-h-72 rounded-xl object-cover border border-gray-200"
                                  />
                                </div>
                              )}
                              {q.has_photo && !q.photo_data && (
                                <div className="pt-4 text-sm text-gray-400 italic">Photo was submitted but is not stored for this question.</div>
                              )}

                              {/* Full question */}
                              <div className="pt-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Full question</p>
                                <p className="text-sm text-gray-800">{q.question_text}</p>
                              </div>

                              {/* AI answer */}
                              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                <p className="text-xs font-semibold text-emerald-800 uppercase mb-1">AI answer</p>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{q.ai_answer}</p>
                              </div>

                              {/* Your answer form */}
                              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                                <p className="text-sm font-semibold text-gray-900">Add your expert note</p>
                                <input
                                  type="text"
                                  placeholder="Your name / label (e.g. Jane Smith, Landscape Designer)"
                                  value={draft.label || ''}
                                  onChange={(e) => setAnswerDraft((d) => ({ ...d, [q.id]: { ...d[q.id], label: e.target.value } }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <textarea
                                  rows={4}
                                  placeholder="Add your practical advice, plant suggestions, or design direction based on what you see…"
                                  value={draft.text || ''}
                                  onChange={(e) => setAnswerDraft((d) => ({ ...d, [q.id]: { ...d[q.id], text: e.target.value } }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                                />
                                <div className="flex items-center gap-3">
                                  <button
                                    type="button"
                                    disabled={draft.saving || !(draft.text || '').trim()}
                                    onClick={() => handlePostAnswer(q.id)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                  >
                                    {draft.saving ? 'Posting…' : 'Submit note for review'}
                                  </button>
                                  <p className="text-xs text-gray-400">John can publish after review</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* New homeowner briefs (public site submissions, not yet opened) */}
        {pendingHomeownerBriefs.length > 0 && (
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-600">
              <div className="flex items-center gap-3 mb-3">
                <ClipboardList className="w-6 h-6 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">New homeowner briefs</h2>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Draft projects from homeowners who sent a yard brief through Find a designer. Open the project to review the photo and client chat.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingHomeownerBriefs.map((p) => {
                  const pd = parseProjectDataJson(p) || {};
                  const b = pd.homeowner_brief || {};
                  return (
                    <div
                      key={p.id}
                      className="border border-green-200 rounded-lg p-4 bg-green-50/50"
                    >
                      <h3 className="font-semibold text-gray-900">{b.homeowner_display_name || p.homeowner_name || 'Homeowner'}</h3>
                      {b.city && (
                        <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                          {b.city}
                        </p>
                      )}
                      {b.main_goal && (
                        <p className="text-sm text-gray-700 mt-2 line-clamp-2">{b.main_goal}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {formatDate(b.submitted_at || p.created_at)}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleOpenHomeownerBrief(p.id)}
                        className="mt-3 w-full px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
                      >
                        Open brief
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Projects Section Header */}
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">My Projects</h2>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-24 w-24 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No projects yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              Get started by creating your first project.
            </p>
            <button
              onClick={handleUploadClick}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              Create Your First Project
            </button>
          </div>
        ) : mainGridProjects.length === 0 ? (
          <p className="text-sm text-gray-600 py-6">
            When you open a homeowner brief above, it will show up in this list.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {mainGridProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden relative group"
              >
                {/* Delete Button */}
                <button
                  onClick={(e) => handleDeleteProject(project.id, project.name || project.project_name, e)}
                  className="absolute top-2 right-2 z-10 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                  title="Delete project"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                
                <div
                  onClick={() => handleProjectClick(project.id)}
                  className="cursor-pointer"
                >
                  {/* Thumbnail */}
                  <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                    {project.plan_image_url ? (
                      <img
                        src={project.plan_image_url}
                        alt={project.name || project.project_name}
                        className="object-cover w-full h-48"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-48 bg-gray-100">
                        <svg
                          className="h-16 w-16 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {project.name || project.project_name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 truncate">
                      {project.homeowner_name || 'No client assigned'}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {formatDate(project.created_at)}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(project.status)}`}>
                        {project.status ? project.status.replace('_', ' ') : 'Draft'}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      {project.plant_count || 0} plants
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Consultation Request Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Yard Consultation Request
              </h2>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Homeowner Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Homeowner Information</h3>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="font-medium">Name:</span> {selectedRequest.homeowner_name || 'Not provided'}
                  </p>
                  <p className="text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span className="font-medium">Email:</span> {selectedRequest.homeowner_email}
                  </p>
                  {selectedRequest.homeowner_phone && (
                    <p className="text-sm flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span className="font-medium">Phone:</span> {selectedRequest.homeowner_phone}
                    </p>
                  )}
                  {selectedRequest.user_location && (
                    <p className="text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium">Location:</span> {selectedRequest.user_location.city || ''} {selectedRequest.user_location.state || ''}
                    </p>
                  )}
                </div>
              </div>

              {/* Yard Photo */}
              {selectedRequest.yard_image_base64 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Yard Photo</h3>
                  <img
                    src={selectedRequest.yard_image_base64}
                    alt="Yard photo"
                    className="w-full rounded-lg border border-gray-200"
                  />
                </div>
              )}

              {/* AI Analysis */}
              {selectedRequest.ai_analysis && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    <h3 className="text-xl font-semibold text-gray-900">AI Analysis & Recommendations</h3>
                  </div>

                  {/* Summary */}
                  {selectedRequest.ai_analysis.summary && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
                      <p className="text-gray-700">{selectedRequest.ai_analysis.summary}</p>
                    </div>
                  )}

                  {/* Current State */}
                  {selectedRequest.ai_analysis.current_state && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Current Yard State</h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        {selectedRequest.ai_analysis.current_state.description && (
                          <p className="text-gray-700 mb-2">{selectedRequest.ai_analysis.current_state.description}</p>
                        )}
                        {selectedRequest.ai_analysis.current_state.sun_exposure && (
                          <p className="text-sm text-gray-600">
                            <strong>Sun Exposure:</strong> {selectedRequest.ai_analysis.current_state.sun_exposure}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Plant Recommendations */}
                  {selectedRequest.ai_analysis.plant_recommendations && selectedRequest.ai_analysis.plant_recommendations.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Plant Recommendations</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {selectedRequest.ai_analysis.plant_recommendations.map((plant, idx) => (
                          <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                            <h5 className="font-semibold text-gray-900">{plant.plant_name}</h5>
                            {plant.botanical_name && (
                              <p className="text-sm text-gray-600 italic">{plant.botanical_name}</p>
                            )}
                            <p className="text-sm text-gray-700 mt-2">{plant.reason}</p>
                            {plant.placement && (
                              <p className="text-xs text-gray-500 mt-1">📍 {plant.placement}</p>
                            )}
                            <button
                              onClick={() => handleViewPlantImages(plant)}
                              className="mt-2 inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 px-2 py-1 rounded hover:bg-gray-50"
                            >
                              <Image className="w-3 h-3" />
                              View images
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Design Suggestions */}
                  {selectedRequest.ai_analysis.design_suggestions && selectedRequest.ai_analysis.design_suggestions.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Design Suggestions</h4>
                      <div className="space-y-3">
                        {selectedRequest.ai_analysis.design_suggestions.map((suggestion, idx) => (
                          <div key={idx} className="bg-green-50 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <h5 className="font-semibold text-gray-900">{suggestion.suggestion}</h5>
                                {suggestion.location && (
                                  <p className="text-sm text-gray-600 mt-1">📍 {suggestion.location}</p>
                                )}
                                {suggestion.benefits && (
                                  <p className="text-sm text-gray-700 mt-1">{suggestion.benefits}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Design Concepts */}
                  {selectedRequest.ai_analysis.design_concepts && selectedRequest.ai_analysis.design_concepts.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Design Concepts</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedRequest.ai_analysis.design_concepts.map((concept, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                          >
                            {concept}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Maintenance Notes */}
                  {selectedRequest.ai_analysis.maintenance_notes && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Maintenance Notes</h4>
                      <p className="text-gray-700 bg-yellow-50 rounded-lg p-3">{selectedRequest.ai_analysis.maintenance_notes}</p>
                    </div>
                  )}

                  {/* Budget Range */}
                  {selectedRequest.ai_analysis.estimated_budget_range && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Estimated Budget Range</h4>
                      <p className="text-gray-700">{selectedRequest.ai_analysis.estimated_budget_range}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                {selectedRequest.status !== 'responded' && (
                  <button
                    onClick={() => {
                      handleMarkResponded(selectedRequest.id);
                      alert('Marked as responded! You can now contact the homeowner via email or phone.');
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Mark as Responded
                  </button>
                )}
                <a
                  href={`mailto:${selectedRequest.homeowner_email}?subject=Re: Yard Consultation Request`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Send Email
                </a>
                {selectedRequest.homeowner_phone && (
                  <a
                    href={`tel:${selectedRequest.homeowner_phone}`}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Call
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collaboration Address Book Modal */}
      {showAddressBook && (
        <AddressBookModal
          collaborators={collaborators}
          loading={loadingCollaborators}
          projects={projects}
          navigate={navigate}
          onClose={() => {
            setShowAddressBook(false);
            setShowInviteModal(false);
          }}
          onDelete={handleDeleteCollaborator}
          onRefresh={loadCollaborators}
          onInvite={() => setShowInviteModal(true)}
          onInviteToProjects={(selectedCollaborators) => {
            setSelectedCollaboratorsForInvite(selectedCollaborators);
            setShowInviteToProjectsModal(true);
          }}
          onEdit={handleEditCollaborator}
        />
      )}

      {/* Invite Selected Collaborators to Projects Modal */}
      {showInviteToProjectsModal && selectedCollaboratorsForInvite.length > 0 && (
        <InviteToProjectsModal
          collaborators={selectedCollaboratorsForInvite}
          projects={projects}
          onInvite={handleInviteToProjects}
          onClose={() => {
            setShowInviteToProjectsModal(false);
            setSelectedCollaboratorsForInvite([]);
          }}
        />
      )}

      {/* Invitation Summary Modal */}
      {showInvitationSummary && invitationResults && (
        <InvitationSummaryModal
          results={invitationResults}
          onClose={() => {
            setShowInvitationSummary(false);
            setInvitationResults(null);
          }}
        />
      )}

      {/* Plant Images Modal */}
      {showImageModal && imageModalPlant && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{imageModalPlant.plant_name}</h3>
              <button onClick={() => setShowImageModal(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {imageModalLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader className="w-10 h-10 animate-spin text-emerald-500" />
                </div>
              ) : imageModalPhotos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {imageModalPhotos.map((photo, idx) => (
                    <div key={idx} className="rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={photo.thumbnail || photo.url}
                        alt={`${imageModalPlant.plant_name} - ${idx + 1}`}
                        className="w-full h-48 object-cover"
                        onError={(e) => { e.target.src = photo.url; }}
                      />
                      {(photo.attribution || photo.photographer) && (
                        <div className="p-2 bg-gray-50 text-xs text-gray-600 truncate" title={photo.attribution || `Photo by ${photo.photographer}`}>
                          {photo.attribution ? photo.attribution.replace(/<[^>]*>/g, '').trim() : `Photo by ${photo.photographer}`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p className="mb-4">No images found. Try searching on Google Images for more results.</p>
                  <a
                    href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(imageModalPlant.plant_name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on Google Images
                  </a>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              {imageModalPhotos.length > 0 ? (
                <a
                  href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(imageModalPlant.plant_name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  See more on Google Images
                </a>
              ) : (
                <span />
              )}
              <button
                onClick={() => setShowImageModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteCollaboratorModal
          projects={projects}
          selectedProjectId={selectedProjectId}
          setSelectedProjectId={setSelectedProjectId}
          inviteMode={inviteMode}
          setInviteMode={setInviteMode}
          inviteName={inviteName}
          setInviteName={setInviteName}
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          inviteType={inviteType}
          setInviteType={setInviteType}
          tradeCategory={tradeCategory}
          setTradeCategory={setTradeCategory}
          messageVisibility={messageVisibility}
          setMessageVisibility={setMessageVisibility}
          scheduleAccess={scheduleAccess}
          setScheduleAccess={setScheduleAccess}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchLocation={searchLocation}
          setSearchLocation={setSearchLocation}
          searchType={searchType}
          setSearchType={setSearchType}
          searchResults={searchResults}
          searching={searching}
          designerLocation={designerLocation}
          onSearch={handleSearchBusinesses}
          onInviteFromSearch={handleInviteFromSearch}
          onSubmit={handleInvite}
          onClose={() => {
            setShowInviteModal(false);
            setInviteName('');
            setInviteEmail('');
            setInviteType('client');
            setTradeCategory('contractor');
            setInviteMode('manual');
            setSearchResults([]);
            setSearchQuery('');
            setSelectedProjectId('');
            setMessageVisibility('all');
            setScheduleAccess(true);
          }}
        />
      )}
    </div>
  );
};

// Address Book Modal Component
const AddressBookModal = ({ collaborators, loading, projects, navigate, onClose, onDelete, onRefresh, onInvite, onInviteToProjects, onEdit }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [selectedCollaborators, setSelectedCollaborators] = useState(new Set());
  const [editingCollaborator, setEditingCollaborator] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');

  // Filter collaborators based on search and project filter
  const filteredCollaborators = collaborators.filter(collab => {
    const matchesSearch = !searchTerm.trim() || 
      collab.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collab.recipient_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesProject = !projectFilter || 
      collab.projects?.some(p => p.project_id === projectFilter || p.project_name?.toLowerCase().includes(projectFilter.toLowerCase()));
    
    return matchesSearch && matchesProject;
  });

  const handleSelectAll = () => {
    if (selectedCollaborators.size === filteredCollaborators.length) {
      setSelectedCollaborators(new Set());
    } else {
      setSelectedCollaborators(new Set(filteredCollaborators.map((_, idx) => idx)));
    }
  };

  const handleSelectCollaborator = (idx) => {
    const newSelected = new Set(selectedCollaborators);
    if (newSelected.has(idx)) {
      newSelected.delete(idx);
    } else {
      newSelected.add(idx);
    }
    setSelectedCollaborators(newSelected);
  };

  const handleInviteSelectedToProjects = () => {
    const selected = Array.from(selectedCollaborators).map(idx => filteredCollaborators[idx]);
    if (selected.length === 0) {
      alert('Please select at least one collaborator');
      return;
    }
    if (onInviteToProjects) {
      onInviteToProjects(selected);
    }
  };

  const handleDeleteSelected = () => {
    const selected = Array.from(selectedCollaborators).map(idx => filteredCollaborators[idx]);
    if (selected.length === 0) {
      alert('Please select at least one collaborator');
      return;
    }
    if (window.confirm(`Remove ${selected.length} collaborator${selected.length > 1 ? 's' : ''}?`)) {
      selected.forEach(collab => onDelete(collab));
      setSelectedCollaborators(new Set());
    }
  };

  const handleEdit = (collab) => {
    setEditingCollaborator(collab);
    setEditName(collab.recipient_name || '');
    setEditEmail(collab.recipient_email || '');
    // Get phone number from first project (they should all have the same phone)
    setEditPhone(collab.projects?.[0]?.phone_number || collab.phone_number || '');
  };

  const handleSaveEdit = () => {
    if (onEdit && editingCollaborator) {
      onEdit(editingCollaborator, { name: editName, email: editEmail, phone: editPhone });
      setEditingCollaborator(null);
      setEditName('');
      setEditEmail('');
      setEditPhone('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Collaboration Address Book</h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage your network of trades, suppliers, and collaborators across all your projects.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>

          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Search by Name or Email</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search name or email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Project</label>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name || project.project_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={onInvite}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Invite New
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedCollaborators.size > 0 && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-medium text-blue-900">
                {selectedCollaborators.size} selected
              </span>
              <button
                onClick={handleInviteSelectedToProjects}
                className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                Invite to Projects
              </button>
              <button
                onClick={handleDeleteSelected}
                className="px-3 py-1.5 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedCollaborators(new Set())}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>

        {/* Collaborators List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading collaborators...</div>
          ) : filteredCollaborators.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p>No collaborators found</p>
              {searchTerm || projectFilter ? (
                <p className="text-sm mt-1">Try adjusting your search or filters</p>
              ) : (
                <p className="text-sm mt-1">Click "Invite New" to invite people</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedCollaborators.size === filteredCollaborators.length && filteredCollaborators.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-600">
                    {filteredCollaborators.length} collaborator{filteredCollaborators.length !== 1 ? 's' : ''}
                    {searchTerm || projectFilter ? ' found' : ' across all projects'}
                  </span>
                </div>
              </div>
              {filteredCollaborators.map((collab, idx) => {
                const isTrade = collab.share_type === 'trade' || collab.trade_type;
                const isSelected = selectedCollaborators.has(idx);
                
                return (
                  <div key={idx} className={`p-4 bg-gray-50 rounded-lg border-2 transition-all ${
                    isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectCollaborator(idx)}
                        className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {editingCollaborator?.recipient_email === collab.recipient_email ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Name"
                              />
                              <input
                                type="email"
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Email"
                              />
                              <input
                                type="tel"
                                value={editPhone}
                                onChange={(e) => setEditPhone(e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Phone (optional)"
                              />
                              <button
                                onClick={handleSaveEdit}
                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingCollaborator(null);
                                  setEditName('');
                                  setEditEmail('');
                                  setEditPhone('');
                                }}
                                className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="font-medium text-gray-900">{collab.recipient_name}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                !isTrade ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {!isTrade ? 'Client' : collab.trade_type || 'Trade'}
                              </span>
                            </>
                          )}
                        </div>
                        {!editingCollaborator || editingCollaborator?.recipient_email !== collab.recipient_email ? (
                          <>
                            <p className="text-sm text-gray-600 truncate">{collab.recipient_email}</p>
                            {(collab.projects?.[0]?.phone_number || collab.phone_number) && (
                              <p className="text-sm text-gray-600 truncate">
                                📞 {collab.projects?.[0]?.phone_number || collab.phone_number}
                              </p>
                            )}
                            {collab.created_at && (
                              <p className="text-xs text-gray-500 mt-1">
                                Invited {new Date(collab.created_at).toLocaleDateString()}
                              </p>
                            )}
                            {/* Notification Preferences */}
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-xs font-medium text-gray-700 mb-1">Notification Method:</p>
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`notification_${collab.recipient_email}`}
                                    value="email"
                                    checked={(collab.notification_method || 'email') === 'email'}
                                    onChange={async (e) => {
                                      try {
                                        const token = localStorage.getItem('authToken');
                                        if (!token) return;
                                        const API_BASE_URL = process.env.REACT_APP_API_URL || 
                                          (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
                                        // Update notification preference for all projects this collaborator is part of
                                        if (collab.projects) {
                                          for (const project of collab.projects) {
                                            if (project.share_id) {
                                              await fetch(`${API_BASE_URL}/collaboration/share/${project.share_id}`, {
                                                method: 'PUT',
                                                headers: {
                                                  'Content-Type': 'application/json',
                                                  'Authorization': `Bearer ${token}`
                                                },
                                                body: JSON.stringify({
                                                  notificationMethod: e.target.value
                                                })
                                              });
                                            }
                                          }
                                        }
                                        if (onRefresh) onRefresh();
                                      } catch (error) {
                                        console.error('Error updating notification preference:', error);
                                      }
                                    }}
                                    className="w-3 h-3 text-green-600 border-gray-300 focus:ring-green-500"
                                  />
                                  <span className="text-xs text-gray-700">Email</span>
                                </label>
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`notification_${collab.recipient_email}`}
                                    value="sms"
                                    checked={(collab.notification_method || 'email') === 'sms'}
                                    onChange={async (e) => {
                                      try {
                                        const token = localStorage.getItem('authToken');
                                        if (!token) return;
                                        const API_BASE_URL = process.env.REACT_APP_API_URL || 
                                          (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
                                        if (collab.projects) {
                                          for (const project of collab.projects) {
                                            if (project.share_id) {
                                              await fetch(`${API_BASE_URL}/collaboration/share/${project.share_id}`, {
                                                method: 'PUT',
                                                headers: {
                                                  'Content-Type': 'application/json',
                                                  'Authorization': `Bearer ${token}`
                                                },
                                                body: JSON.stringify({
                                                  notificationMethod: e.target.value
                                                })
                                              });
                                            }
                                          }
                                        }
                                        if (onRefresh) onRefresh();
                                      } catch (error) {
                                        console.error('Error updating notification preference:', error);
                                      }
                                    }}
                                    className="w-3 h-3 text-green-600 border-gray-300 focus:ring-green-500"
                                  />
                                  <span className="text-xs text-gray-700">SMS</span>
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 italic">
                                Choose how this collaborator receives message notifications (SMS requires phone number)
                              </p>
                            </div>
                            {/* Show projects with accessed status */}
                            {collab.projects && collab.projects.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs font-medium text-gray-700 mb-2">Projects:</p>
                                <div className="space-y-1">
                                  {collab.projects.map((project, pIdx) => {
                                    // Find the project name from the projects list
                                    const fullProject = projects.find(p => p.id === project.project_id);
                                    const projectName = project.project_name || fullProject?.name || fullProject?.project_name || 'this project';
                                    const shareToken = project.share_token || project.shareToken;
                                    
                                    return (
                                      <div key={pIdx} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          {fullProject ? (
                                            <button
                                              onClick={() => navigate(`/plan/${project.project_id}`)}
                                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                                            >
                                              {projectName}
                                            </button>
                                          ) : (
                                            <span className="text-sm text-gray-600 truncate">{projectName}</span>
                                          )}
                                          {project.accessed_at ? (
                                            <span className="text-xs text-green-600 flex items-center gap-1 whitespace-nowrap">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                              </svg>
                                              Viewed {new Date(project.accessed_at).toLocaleDateString()}
                                            </span>
                                          ) : (
                                            <span className="text-xs text-gray-400 whitespace-nowrap">Not viewed yet</span>
                                          )}
                                        </div>
                                        {shareToken && (
                                          <button
                                            onClick={() => {
                                              const link = `${window.location.origin}/shared/${shareToken}/communication`;
                                              const subject = encodeURIComponent(`Invitation to collaborate on ${projectName}`);
                                              const body = encodeURIComponent(
                                                `Hi ${collab.recipient_name},\n\n` +
                                                `You have been invited to collaborate on the project "${projectName}".\n\n` +
                                                `Please click the link below to access the project:\n\n` +
                                                `${link}\n\n` +
                                                `Looking forward to working with you!`
                                              );
                                              const mailtoLink = `mailto:${collab.recipient_email}?subject=${subject}&body=${body}`;
                                              window.location.href = mailtoLink;
                                            }}
                                            className="ml-2 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 whitespace-nowrap flex-shrink-0"
                                            title="Resend invitation via email"
                                          >
                                            Resend Invite
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        ) : null}
                      </div>
                      {(!editingCollaborator || editingCollaborator?.recipient_email !== collab.recipient_email) && (
                        <div className="flex gap-2 ml-4 flex-shrink-0">
                          <button
                            onClick={() => handleEdit(collab)}
                            className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                            title="Edit collaborator"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => onDelete(collab)}
                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                            title="Remove collaborator"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Invite Selected Collaborators to Projects Modal
const InviteToProjectsModal = ({ collaborators, projects, onInvite, onClose }) => {
  const [selectedProjectIds, setSelectedProjectIds] = useState(new Set());
  const [inviteType, setInviteType] = useState('trade');
  const [tradeCategory, setTradeCategory] = useState('contractor');
  const [messageVisibility, setMessageVisibility] = useState('all');
  const [scheduleAccess, setScheduleAccess] = useState(true);
  const [inviting, setInviting] = useState(false);

  const handleToggleProject = (projectId) => {
    const newSelected = new Set(selectedProjectIds);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjectIds(newSelected);
  };

  const handleSelectAllProjects = () => {
    if (selectedProjectIds.size === projects.length) {
      setSelectedProjectIds(new Set());
    } else {
      setSelectedProjectIds(new Set(projects.map(p => p.id)));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedProjectIds.size === 0) {
      alert('Please select at least one project');
      return;
    }

    setInviting(true);
    try {
      await onInvite(
        Array.from(selectedProjectIds),
        inviteType,
        tradeCategory,
        messageVisibility,
        scheduleAccess
      );
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Invite to Projects</h2>
              <p className="text-sm text-gray-600 mt-1">
                Invite {collaborators.length} collaborator{collaborators.length > 1 ? 's' : ''} to selected projects
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Select Projects</label>
              <button
                type="button"
                onClick={handleSelectAllProjects}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedProjectIds.size === projects.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {projects.map(project => (
                <label key={project.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedProjectIds.has(project.id)}
                    onChange={() => handleToggleProject(project.id)}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-900">{project.name || project.project_name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Invite Type (only show if all selected are trades) */}
          {collaborators.every(c => c.share_type === 'trade' || c.trade_type) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Trade Category</label>
              <select
                value={tradeCategory}
                onChange={(e) => setTradeCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              >
                <option value="contractor">Contractor</option>
                <option value="supplier">Supplier</option>
                <option value="installer">Installer</option>
                <option value="consultant">Consultant</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={inviting || selectedProjectIds.size === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inviting ? 'Inviting...' : `Invite to ${selectedProjectIds.size} Project${selectedProjectIds.size > 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Invitation Summary Modal
const InvitationSummaryModal = ({ results, onClose }) => {
  const { successful, failed } = results;
  const totalInvitations = successful.length;
  const uniqueCollaborators = new Set(successful.map(s => s.collaborator.recipient_email));
  const uniqueProjects = new Set(successful.map(s => s.project?.id || s.project?.project_id));

  const handleCopyAllLinks = () => {
    const allLinks = successful.map(s => 
      `${s.collaborator.recipient_name} (${s.project?.name || s.project?.project_name}): ${s.shareUrl}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(allLinks);
    alert('All invitation links copied to clipboard!');
  };

  const handleCopyLink = (shareUrl) => {
    navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard!');
  };

  const handleMailto = (collaborator, project, shareUrl) => {
    const subject = encodeURIComponent(`Invitation to collaborate on ${project?.name || project?.project_name || 'project'}`);
    const body = encodeURIComponent(
      `Hi ${collaborator.recipient_name},\n\n` +
      `I've invited you to collaborate on the project "${project?.name || project?.project_name || 'project'}".\n\n` +
      `Click the link below to access the project:\n${shareUrl}\n\n` +
      `Looking forward to working with you!`
    );
    window.location.href = `mailto:${collaborator.recipient_email}?subject=${subject}&body=${body}`;
  };

  const handleSendAllMailtos = () => {
    // Group by collaborator email
    const byEmail = {};
    successful.forEach(s => {
      if (!byEmail[s.collaborator.recipient_email]) {
        byEmail[s.collaborator.recipient_email] = {
          collaborator: s.collaborator,
          projects: []
        };
      }
      byEmail[s.collaborator.recipient_email].projects.push({
        project: s.project,
        shareUrl: s.shareUrl
      });
    });

    // Create mailto for each unique collaborator
    Object.values(byEmail).forEach(({ collaborator, projects }) => {
      const projectNames = projects.map(p => p.project?.name || p.project?.project_name || 'project').join(', ');
      const projectLinks = projects.map(p => 
        `${p.project?.name || p.project?.project_name || 'project'}: ${p.shareUrl}`
      ).join('\n\n');
      
      const subject = encodeURIComponent(`Invitation to collaborate on ${projects.length > 1 ? 'multiple projects' : projectNames}`);
      const body = encodeURIComponent(
        `Hi ${collaborator.recipient_name},\n\n` +
        `I've invited you to collaborate on ${projects.length > 1 ? 'the following projects' : `the project "${projectNames}"`}:\n\n` +
        `${projectLinks}\n\n` +
        `Looking forward to working with you!`
      );
      
      // Open mailto (will open default email client)
      window.location.href = `mailto:${collaborator.recipient_email}?subject=${subject}&body=${body}`;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Invitations Sent</h2>
              <p className="text-sm text-gray-600 mt-1">
                Successfully created {totalInvitations} invitation{totalInvitations !== 1 ? 's' : ''} for {uniqueCollaborators.size} collaborator{uniqueCollaborators.size !== 1 ? 's' : ''} across {uniqueProjects.size} project{uniqueProjects.size !== 1 ? 's' : ''}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Bulk Actions */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">Quick Actions:</span>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyAllLinks}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Copy All Links
                </button>
                <button
                  onClick={handleSendAllMailtos}
                  className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  Open Email Client
                </button>
              </div>
            </div>
          </div>

          {/* Successful Invitations */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Invitation Details:</h3>
            {successful.map((invitation, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{invitation.collaborator.recipient_name}</span>
                      <span className="text-sm text-gray-500">({invitation.collaborator.recipient_email})</span>
                    </div>
                    <p className="text-sm font-medium text-gray-700">
                      Project: {invitation.project?.name || invitation.project?.project_name || 'Unknown'}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleCopyLink(invitation.shareUrl)}
                      className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                      title="Copy link"
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={() => handleMailto(invitation.collaborator, invitation.project, invitation.shareUrl)}
                      className="px-3 py-1.5 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200"
                      title="Open email client"
                    >
                      Email
                    </button>
                  </div>
                </div>
                <div className="mt-2 p-2 bg-white rounded border border-gray-300">
                  <p className="text-xs text-gray-500 mb-1">Invitation Link:</p>
                  <p className="text-xs text-blue-600 break-all">{invitation.shareUrl}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Failed Invitations */}
          {failed.length > 0 && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <h3 className="text-sm font-semibold text-red-900 mb-2">Failed Invitations ({failed.length}):</h3>
              <div className="space-y-1">
                {failed.map((failure, idx) => (
                  <p key={idx} className="text-xs text-red-700">
                    {failure.collaborator.recipient_name} → {failure.project?.name || failure.project?.project_name || 'Unknown'} (may already be invited)
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// OLD COMPONENT REMOVED - Now using shared component from ./components/InviteCollaboratorModal
// The old inline component has been removed and replaced with the shared component

// Endorsement Card Component (kept for backward compatibility, but not used in new Address Book)
const EndorsementCard = ({ endorsement, onRemove }) => {
  const [showInFindDesigner, setShowInFindDesigner] = useState(endorsement.status === 'active');
  const [updating, setUpdating] = useState(false);

  const handleToggleVisibility = async () => {
    const newStatus = showInFindDesigner ? 'removed' : 'active';
    setUpdating(true);
    try {
      await api.endorsementsAPI.update(endorsement.id, newStatus);
      setShowInFindDesigner(!showInFindDesigner);
    } catch (err) {
      console.error('Error updating endorsement visibility:', err);
      alert('Failed to update visibility. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-gray-900">
              {endorsement.business_name || endorsement.endorsed_name}
            </h3>
            {endorsement.endorsed_type && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full capitalize">
                {endorsement.endorsed_type}
              </span>
            )}
          </div>
          <div className="space-y-1 text-sm text-gray-600">
            {endorsement.endorsed_email && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>{endorsement.endorsed_email}</span>
              </div>
            )}
            {endorsement.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>{endorsement.phone}</span>
              </div>
            )}
            {(endorsement.city || endorsement.province_state) && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>
                  {endorsement.city}
                  {endorsement.province_state && `, ${endorsement.province_state}`}
                  {endorsement.country && `, ${endorsement.country}`}
                </span>
              </div>
            )}
            {endorsement.website && (
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                <a
                  href={endorsement.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {endorsement.website}
                </a>
              </div>
            )}
            {endorsement.notes && (
              <p className="text-gray-700 mt-2 italic">"{endorsement.notes}"</p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 ml-4">
          <button
            onClick={handleToggleVisibility}
            disabled={updating}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm disabled:opacity-50 ${
              showInFindDesigner
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title={showInFindDesigner ? 'Hide from Find a Designer' : 'Show in Find a Designer'}
          >
            {updating ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                Updating...
              </>
            ) : showInFindDesigner ? (
              <>
                <Eye className="w-4 h-4" />
                Visible
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4" />
                Hidden
              </>
            )}
          </button>
          <button
            onClick={() => onRemove(endorsement.id)}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 text-sm"
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
        </div>
      </div>
      {endorsement.source && (
        <p className="text-xs text-gray-500 mt-2">
          Added via: {endorsement.source === 'invitation' ? 'Project Invitation' : 
                      endorsement.source === 'local_business' ? 'Local Business' : 'Manual'}
        </p>
      )}
    </div>
  );
};

// Add Endorsement Modal Component
const AddEndorsementModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    endorsed_email: '',
    endorsed_name: '',
    business_name: '',
    endorsed_type: 'trade',
    city: '',
    province_state: '',
    country: '',
    phone: '',
    website: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.endorsed_email || !formData.endorsed_name) {
      alert('Email and name are required');
      return;
    }

    setSaving(true);
    try {
      await api.endorsementsAPI.create(formData);
      onSuccess();
    } catch (err) {
      console.error('Error creating endorsement:', err);
      alert('Failed to add endorsement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Add to Address Book</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.endorsed_name}
                  onChange={(e) => setFormData({ ...formData, endorsed_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.endorsed_email}
                  onChange={(e) => setFormData({ ...formData, endorsed_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name
              </label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.endorsed_type}
                onChange={(e) => setFormData({ ...formData, endorsed_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="trade">Trade / Contractor</option>
                <option value="supplier">Supplier</option>
                <option value="collaborator">Collaborator</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Province/State</label>
                <input
                  type="text"
                  value={formData.province_state}
                  onChange={(e) => setFormData({ ...formData, province_state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Optional notes about this collaborator..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
              >
                {saving ? 'Adding...' : 'Add to Address Book'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Search Business Modal Component
const SearchBusinessModal = ({ onClose, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/google-business/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setResults(data.businesses || []);
    } catch (err) {
      console.error('Error searching businesses:', err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Lookup Business</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="Search for business name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {results.length > 0 && (
            <div className="space-y-2">
              {results.map((business, idx) => (
                <div
                  key={idx}
                  onClick={() => onSelect(business)}
                  className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <h3 className="font-semibold">{business.name}</h3>
                  {business.address && <p className="text-sm text-gray-600">{business.address}</p>}
                  {business.phone && <p className="text-sm text-gray-600">{business.phone}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignerDashboard;
